import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getToken } from "../lib/auth";
import { applySeo } from "../lib/seo";
import "../styles/sportly-tokens.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 1200;
const GUTTER = 32;

// ---------------------------------------------------------------
// CTA button helper — renders an <a> for #anchor/http/mailto, or
// an SPA-navigating <button> for internal "/" paths.
// ---------------------------------------------------------------
function CTAButton({ href, fallbackHref, className, children, testid, navigate, style }) {
  const target = href || fallbackHref || "#";
  const isInternal = target.startsWith("/");
  const isAnchor = target.startsWith("#");
  const isExternal = /^(https?:|mailto:|tel:)/i.test(target);

  if (isInternal) {
    return (
      <button
        type="button"
        className={className}
        style={style}
        onClick={() => navigate(target)}
        data-testid={testid}
      >
        {children}
      </button>
    );
  }
  // Anchors (#...) scroll within the page; external links open in same/new tab.
  return (
    <a
      href={target}
      className={className}
      style={style}
      target={isExternal && !target.startsWith("mailto:") ? "_blank" : undefined}
      rel={isExternal && !target.startsWith("mailto:") ? "noreferrer" : undefined}
      data-testid={testid}
    >
      {children}
    </a>
  );
}


// ---------------------------------------------------------------
// Tiny inline icon set (Lucide-style, currentColor)
// ---------------------------------------------------------------
function Icon({ name, size = 24, color = "currentColor", stroke = 1.75 }) {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: stroke,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const map = {
    check: <svg {...p}><path d="M5 12l5 5L20 7" /></svg>,
    bell: <svg {...p}><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>,
    play: <svg {...p}><polygon points="6 4 20 12 6 20 6 4" /></svg>,
    arrow: <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" /></svg>,
    apple: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M16.5 12.7c0-2.6 2.2-3.9 2.3-3.9-1.3-1.8-3.2-2.1-3.9-2.1-1.7-.2-3.3 1-4.1 1-.9 0-2.2-1-3.6-1-1.8 0-3.6 1.1-4.5 2.8-1.9 3.3-.5 8.3 1.4 11 .9 1.3 2 2.8 3.4 2.8 1.4-.1 1.9-.9 3.6-.9 1.7 0 2.2.9 3.6.8 1.5 0 2.4-1.3 3.4-2.7 1-1.5 1.5-2.9 1.5-3 0 0-2.9-1.1-2.9-4.4zM13.6 4.8C14.4 3.9 14.9 2.7 14.7 1.5c-1 .1-2.3.7-3.1 1.6-.7.8-1.4 2-1.2 3.2 1.2.1 2.4-.6 3.2-1.5z" /></svg>,
    google: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M3 3v18l9-9zM3 3l9 9 4-4-13-5zM3 21l13-5-4-4-9 9z" /></svg>,
    chevron: <svg {...p}><path d="M6 9l6 6 6-6" /></svg>,
  };
  return map[name] || null;
}

// ---------------------------------------------------------------
// 1. NAV
// ---------------------------------------------------------------
function Nav({ data, isAuthed, navigate }) {
  return (
    <div
      data-testid="marketing-nav"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        background: "rgba(247,245,238,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--mist-200)",
      }}
    >
      <div
        className="section-pad"
        style={{
          maxWidth: MAX,
          margin: "0 auto",
          padding: `14px ${GUTTER}px`,
          display: "flex",
          alignItems: "center",
          gap: 28,
        }}
      >
        <a href="#top" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "var(--pitch-600)",
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--sportly-font-display)",
              fontWeight: 900,
              fontSize: 22,
              color: "#F7F5EE",
            }}
          >
            {data.logo_letter || "S"}
            <span
              style={{
                position: "absolute",
                bottom: -3,
                right: -3,
                width: 10,
                height: 10,
                borderRadius: 999,
                background: "var(--strike-500)",
                border: "2px solid var(--chalk-50)",
              }}
            />
          </div>
          <span
            style={{
              fontFamily: "var(--sportly-font-display)",
              fontWeight: 900,
              fontSize: 24,
              textTransform: "uppercase",
              letterSpacing: "-0.005em",
            }}
          >
            {data.brand}
          </span>
        </a>
        <div
          className="nav-links"
          style={{
            display: "flex",
            gap: 28,
            marginLeft: 32,
            fontSize: 15,
            fontWeight: 500,
            color: "var(--ink-700)",
          }}
        >
          {(data.links || []).map((l, i) => (
            <a key={i} href={l.href} data-testid={`nav-link-${i}`}>
              {l.label}
            </a>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        {isAuthed ? (
          <CTAButton
            href={data.dashboard_href}
            fallbackHref="/overview"
            className="btn btn-pitch"
            navigate={navigate}
            testid="nav-dashboard-btn"
          >
            {data.dashboard_label || "Naar mijn dashboard"}
          </CTAButton>
        ) : (
          <>
            <CTAButton
              href={data.login_href}
              fallbackHref="/login"
              className="btn btn-ghost-bordered"
              navigate={navigate}
              testid="nav-login-btn"
            >
              {data.login_label}
            </CTAButton>
            <CTAButton
              href={data.cta_href}
              fallbackHref="/abonnementen"
              className="btn btn-pitch"
              navigate={navigate}
              testid="nav-cta-btn"
            >
              {data.cta_label}
            </CTAButton>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// 2. HERO
// ---------------------------------------------------------------
function Hero({ data, navigate }) {
  return (
    <section
      id="top"
      className="section-pad"
      style={{ padding: `72px ${GUTTER}px 80px`, position: "relative", overflow: "hidden" }}
    >
      <div
        className="hero-grid"
        style={{
          maxWidth: MAX,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "var(--pitch-50)",
              color: "var(--pitch-700)",
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 24,
              border: "1px solid var(--pitch-100)",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--pitch-600)" }} />
            {data.badge}
          </div>
          <h1 className="display hero-h1" style={{ fontSize: 88, marginBottom: 20 }} data-testid="hero-headline">
            {data.headline_line1}
            <br />
            {data.headline_line2}
          </h1>
          <p style={{ fontSize: 20, lineHeight: 1.5, color: "var(--ink-700)", maxWidth: 520, marginBottom: 32 }}>
            {data.paragraph}
          </p>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <CTAButton
              href={data.primary_href}
              fallbackHref="/abonnementen"
              className="btn btn-strike"
              navigate={navigate}
              testid="hero-primary-cta"
            >
              {data.primary_cta} <Icon name="arrow" size={18} />
            </CTAButton>
            <a href={data.secondary_href || "#functies"} className="btn btn-ghost-bordered btn-lg" data-testid="hero-demo-btn">
              <Icon name="play" size={16} /> {data.secondary_cta}
            </a>
          </div>
          <div style={{ display: "flex", gap: 18, fontSize: 13, color: "var(--ink-500)", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon name="check" size={14} color="var(--pitch-600)" stroke={3} /> {data.trust1}
            </span>
            <span style={{ width: 3, height: 3, borderRadius: 999, background: "var(--ink-300)" }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon name="check" size={14} color="var(--pitch-600)" stroke={3} /> {data.trust2}
            </span>
          </div>
        </div>
        {data.mock_image ? (
          <img
            src={data.mock_image}
            alt="Sportly app preview"
            style={{
              width: "100%",
              borderRadius: 20,
              border: "1px solid var(--mist-200)",
              boxShadow: "var(--shadow-lg)",
              display: "block",
            }}
          />
        ) : (
          <HeroMock />
        )}
      </div>
    </section>
  );
}

function HeroMock() {
  const rows = [
    { date: "07 mrt", tu: "Thuis", opp: "Almere Buite…", team: "JO14-2", time: "12:20", start: "12:50", badges: { simon: { l: "N/A", c: "var(--danger-500)" }, felix: { l: "F", c: "#A855F7" } } },
    { date: "14 mrt", tu: "Uit", opp: "Nijkerk", team: "JO14-1", time: "15:00", start: "16:00", badges: { hugo: { l: "R", c: "var(--strike-500)" } } },
    { date: "21 mrt", tu: "Thuis", opp: "Huizen", team: "JO14-2", time: "14:10", start: "14:40", badges: { alex: { l: "F", c: "#A855F7" } } },
    { date: "28 mrt", tu: "Thuis", opp: "Gooische", team: "JO14-6", time: "12:10", start: "12:40", badges: {} },
    { date: "11 apr", tu: "Uit", opp: "Laren", team: "JO14-4", time: "ntb", start: "—", badges: { hugo: { l: "R", c: "var(--strike-500)" } } },
  ];
  const legend = [
    { l: "K", c: "var(--ink-900)" },
    { l: "R", c: "var(--strike-500)" },
    { l: "F", c: "#A855F7" },
    { l: "C", c: "var(--pitch-600)" },
    { l: "N/A", c: "var(--danger-500)" },
  ];
  return (
    <div style={{ position: "relative" }}>
      <div style={{ background: "var(--surface)", border: "1px solid var(--mist-200)", borderRadius: 20, overflow: "hidden", boxShadow: "var(--shadow-lg)" }}>
        <div style={{ height: 36, background: "var(--chalk-100)", borderBottom: "1px solid var(--mist-200)", display: "flex", alignItems: "center", padding: "0 14px", gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#FC615D" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#FDBE40" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#34C84A" }} />
          <div style={{ flex: 1, textAlign: "center", fontSize: 11, color: "var(--ink-500)" }}>HC Naarden / JO14-5 — Overzicht</div>
        </div>
        <div style={{ padding: "24px 28px 8px" }}>
          <div style={{ fontSize: 11, color: "var(--pitch-600)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>HC NAARDEN / JO14-5</div>
          <div className="display" style={{ fontSize: 44, marginTop: 4 }}>Overzicht</div>
          <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center", flexWrap: "wrap", fontSize: 11 }}>
            <span style={{ color: "var(--ink-500)", fontWeight: 600 }}>Legenda:</span>
            {legend.map((b, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ink-700)", fontSize: 11 }}>
                <span style={{ background: b.c, color: "#fff", fontWeight: 800, padding: "1px 7px", borderRadius: 4, fontSize: 10, fontFamily: "var(--sportly-font-display)" }}>{b.l}</span>
                {b.l === "K" ? "Keept" : b.l === "R" ? "Rijdt" : b.l === "F" ? "Fluit" : b.l === "C" ? "Coach" : "Afwezig"}
              </span>
            ))}
          </div>
        </div>
        <div style={{ padding: "8px 12px 18px" }}>
          <div style={{ border: "1px solid var(--mist-200)", borderRadius: 12, overflow: "hidden", background: "var(--surface)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "62px 50px 1fr 64px 64px 60px 50px 50px 50px 50px", alignItems: "center", padding: "8px 12px", background: "var(--chalk-50)", borderBottom: "1px solid var(--mist-200)", fontSize: 9.5, fontWeight: 700, color: "var(--ink-500)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              <span>Datum</span><span>T/U</span><span>Tegenstander</span><span>Team</span><span>Verzam.</span><span>Aanv.</span>
              {["Hugo", "Simon", "Alex", "Felix"].map((p) => <span key={p}>{p.slice(0, 5)}</span>)}
            </div>
            {rows.map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "62px 50px 1fr 64px 64px 60px 50px 50px 50px 50px", alignItems: "center", padding: "9px 12px", borderBottom: i < rows.length - 1 ? "1px solid var(--mist-100)" : "none", fontSize: 11, color: "var(--ink-900)" }}>
                <span style={{ fontFamily: "var(--sportly-font-display)", fontWeight: 700, fontSize: 13 }}>{r.date}</span>
                <span style={{ color: r.tu === "Thuis" ? "var(--pitch-600)" : "var(--strike-500)", fontWeight: 600, fontSize: 11 }}>{r.tu}</span>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.opp}</span>
                <span style={{ color: "var(--ink-700)" }}>{r.team}</span>
                <span style={{ fontFamily: "var(--sportly-font-display)", fontWeight: 700, color: r.time === "ntb" ? "var(--ink-400)" : "var(--ink-900)", fontStyle: r.time === "ntb" ? "italic" : "normal" }}>{r.time}</span>
                <span style={{ fontFamily: "var(--sportly-font-display)", fontWeight: 700 }}>{r.start}</span>
                {["hugo", "simon", "alex", "felix"].map((p) => (
                  <span key={p}>
                    {r.badges[p] ? (
                      <span style={{ background: r.badges[p].c, color: "#fff", fontWeight: 800, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontFamily: "var(--sportly-font-display)" }}>{r.badges[p].l}</span>
                    ) : (
                      <span style={{ color: "var(--ink-300)" }}>–</span>
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", right: -14, bottom: -22, background: "var(--ink-900)", color: "#fff", padding: "12px 16px", borderRadius: 14, boxShadow: "var(--shadow-pop)", display: "flex", alignItems: "center", gap: 12, maxWidth: 280 }}>
        <div style={{ width: 32, height: 32, borderRadius: 999, background: "var(--strike-500)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="bell" size={16} color="#fff" stroke={2.2} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700 }}>Hugo rijdt zaterdag</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Push gestuurd · 12 spelers</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// 3. SOCIAL PROOF
// ---------------------------------------------------------------
function SocialProof({ data }) {
  const stats = data.stats || [];
  const clubs = data.clubs || [];
  return (
    <section
      className="section-pad"
      style={{
        padding: `40px ${GUTTER}px 60px`,
        borderTop: "1px solid var(--mist-200)",
        borderBottom: "1px solid var(--mist-200)",
        background: "var(--surface)",
      }}
    >
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div
          className="stats-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24, paddingBottom: 32, borderBottom: "1px solid var(--mist-200)" }}
        >
          {stats.map((s, i) => (
            <div key={i} style={{ paddingLeft: i ? 24 : 0, borderLeft: i ? "1px solid var(--mist-200)" : "none" }}>
              <div className="display" style={{ fontSize: 54, lineHeight: 0.95, color: "var(--ink-900)" }}>{s.number}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <div style={{ fontSize: 13, color: "var(--ink-500)", fontWeight: 500 }}>{s.label}</div>
                {s.sub && <div style={{ color: "var(--strike-500)", letterSpacing: "-1px", fontSize: 13 }}>{s.sub}</div>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ paddingTop: 28, display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-500)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {data.logos_label}
          </div>
          <div style={{ display: "flex", gap: 28, alignItems: "center", flex: 1, flexWrap: "wrap" }}>
            {clubs.map((c, i) => (
              <ClubLogo key={i} club={c} variant={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ClubLogo({ club, variant }) {
  const shapes = [
    <svg key="s0" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3v18" /></svg>,
    <svg key="s1" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 3 21 8 21 16 12 21 3 16 3 8" /></svg>,
    <svg key="s2" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" /></svg>,
    <svg key="s3" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M4 12h16M12 4v16" /></svg>,
    <svg key="s4" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 3l9 9-9 9-9-9z" /><circle cx="12" cy="12" r="3" /></svg>,
    <svg key="s5" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="9" r="6" /><path d="M9 14l-3 7M15 14l3 7" /></svg>,
  ];
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "var(--ink-500)" }}>
      {club.logo ? (
        <img src={club.logo} alt={club.name} style={{ width: 24, height: 24, objectFit: "contain" }} />
      ) : (
        shapes[variant % shapes.length]
      )}
      <span
        style={{
          fontFamily: "var(--sportly-font-display)",
          fontWeight: 800,
          fontSize: 16,
          textTransform: "uppercase",
          letterSpacing: "-0.005em",
          color: "var(--ink-700)",
        }}
      >
        {club.name}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------
// 4. FEATURES PER ROLE
// ---------------------------------------------------------------
function Features({ data }) {
  const roles = (data.roles || []).filter((r) => r?.enabled !== false);
  const [activeName, setActiveName] = useState(roles[0]?.name || "Manager");
  // Keep activeName in sync when roles list changes (e.g. an enabled role is turned off)
  useEffect(() => {
    if (roles.length === 0) return;
    if (!roles.find((r) => r.name === activeName)) {
      setActiveName(roles[0].name);
    }
  }, [roles, activeName]);
  const active = roles.find((r) => r.name === activeName) || roles[0];
  const items = active?.items || [];
  if (roles.length === 0) return null;
  return (
    <section
      id="functies"
      className="section-pad"
      style={{ padding: `80px ${GUTTER}px`, background: "var(--chalk-50)" }}
    >
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginBottom: 48 }}>
          <div>
            <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14 }}>{data.eyebrow}</div>
            <h2 className="display section-h2" style={{ fontSize: 56, maxWidth: 680 }}>
              {data.headline_line1}<br />{data.headline_line2}
            </h2>
            <p style={{ fontSize: 16, color: "var(--ink-700)", maxWidth: 520, marginTop: 14 }}>
              {data.paragraph}
            </p>
          </div>
          {roles.length > 1 && (
            <div role="tablist" style={{ display: "inline-flex", gap: 4, background: "var(--surface)", border: "1px solid var(--mist-200)", padding: 4, borderRadius: 999, flexWrap: "wrap" }}>
              {roles.map((r) => (
                <button
                  key={r.name}
                  type="button"
                  role="tab"
                  aria-selected={activeName === r.name}
                  onClick={() => setActiveName(r.name)}
                  data-testid={`features-tab-${r.name.toLowerCase()}`}
                  style={{
                    border: 0,
                    padding: "10px 18px",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: "var(--sportly-font-body)",
                    fontSize: 14,
                    fontWeight: 600,
                    background: activeName === r.name ? "var(--ink-900)" : "transparent",
                    color: activeName === r.name ? "#fff" : "var(--ink-700)",
                    transition: "all 160ms cubic-bezier(0.2,0.7,0.2,1)",
                  }}
                >
                  {r.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {items.map((it, i) => (
            <Reveal key={activeName + i} delay={i * 80}>
              <FeatureRow item={it} flip={i % 2 === 1} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureRow({ item, flip, index }) {
  const Text = (
    <div style={{ padding: "0 8px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span className="display" style={{ fontSize: 18, color: "var(--strike-500)", lineHeight: 1 }}>0{index + 1}</span>
        <span className="eyebrow" style={{ color: "var(--ink-500)" }}>{item.eyebrow}</span>
      </div>
      <h3 className="display" style={{ fontSize: 40, margin: "0 0 16px", maxWidth: 460 }}>{item.title}</h3>
      <p style={{ fontSize: 16, color: "var(--ink-700)", lineHeight: 1.6, margin: "0 0 22px", maxWidth: 500 }}>{item.body}</p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {(item.bullets || []).map((b, j) => (
          <li key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "var(--ink-700)" }}>
            <span style={{ flexShrink: 0, marginTop: 4 }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="var(--pitch-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5L20 7" />
              </svg>
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const Shot = (
    <div
      style={{
        background: "linear-gradient(135deg, var(--pitch-50), var(--chalk-100))",
        borderRadius: 20,
        border: "1px solid var(--mist-200)",
        overflow: "hidden",
        aspectRatio: "4 / 3",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {item.image ? (
        <img src={item.image} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--ink-500)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            textAlign: "center",
            padding: "0 24px",
          }}
        >
          App screenshot
          <br />
          {item.eyebrow}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="features-row"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--mist-200)",
        borderRadius: 24,
        padding: 24,
        display: "grid",
        gridTemplateColumns: flip ? "1fr 1.05fr" : "1.05fr 1fr",
        gap: 32,
        alignItems: "stretch",
      }}
    >
      {flip ? Text : Shot}
      {flip ? Shot : Text}
    </div>
  );
}

// ---------------------------------------------------------------
// 5. HOW IT WORKS
// ---------------------------------------------------------------
function HowItWorks({ data }) {
  const steps = data.steps || [];
  return (
    <section className="section-pad" style={{ padding: `80px ${GUTTER}px`, background: "var(--surface)" }}>
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14 }}>{data.eyebrow}</div>
          <h2 className="display section-h2" style={{ fontSize: 56 }}>{data.headline}</h2>
        </div>
        <div className="features-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32, position: "relative" }}>
          <div style={{ position: "absolute", top: 36, left: "16.7%", right: "16.7%", height: 2, background: "var(--mist-200)", zIndex: 0 }} />
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 120} style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 999,
                  background: "var(--pitch-600)",
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--sportly-font-display)",
                  fontWeight: 900,
                  fontSize: 36,
                  marginBottom: 24,
                  border: "4px solid var(--surface)",
                  boxShadow: "0 0 0 1px var(--mist-200)",
                }}
              >
                {s.number}
              </div>
              <div className="display" style={{ fontSize: 28, marginBottom: 10 }}>{s.title}</div>
              <div style={{ fontSize: 15, color: "var(--ink-700)", lineHeight: 1.55, maxWidth: 320, margin: "0 auto" }}>{s.body}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// 6. PRICING
// ---------------------------------------------------------------
function Pricing({ data, navigate }) {
  const [yearly, setYearly] = useState(false);
  const tiers = data.tiers || [];
  return (
    <section id="prijzen" className="section-pad" style={{ padding: `80px ${GUTTER}px`, background: "var(--chalk-50)" }}>
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14 }}>{data.eyebrow}</div>
          <h2 className="display section-h2" style={{ fontSize: 56 }}>{data.headline}</h2>
          <p style={{ fontSize: 16, color: "var(--ink-700)", marginTop: 12 }}>{data.subtitle}</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
          <div style={{ display: "inline-flex", background: "var(--surface)", border: "1px solid var(--mist-200)", padding: 4, borderRadius: 999 }}>
            {[
              { k: false, l: data.toggle_monthly },
              { k: true, l: data.toggle_yearly },
            ].map((opt) => (
              <button
                key={String(opt.k)}
                type="button"
                onClick={() => setYearly(opt.k)}
                data-testid={`pricing-toggle-${opt.k ? "yearly" : "monthly"}`}
                style={{
                  border: 0,
                  padding: "10px 18px",
                  borderRadius: 999,
                  cursor: "pointer",
                  fontFamily: "var(--sportly-font-body)",
                  fontSize: 14,
                  fontWeight: 600,
                  background: yearly === opt.k ? "var(--pitch-600)" : "transparent",
                  color: yearly === opt.k ? "#fff" : "var(--ink-700)",
                  transition: "all 160ms cubic-bezier(0.2,0.7,0.2,1)",
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }}>
          {tiers.map((t, i) => {
            const price = yearly ? t.price_yearly : t.price_monthly;
            const sub = yearly ? t.sub_yearly : t.sub_monthly;
            return (
              <Reveal
                key={i}
                delay={i * 100}
                style={{
                  background: t.highlight ? "var(--ink-900)" : "var(--surface)",
                  color: t.highlight ? "#fff" : "var(--ink-900)",
                  border: t.highlight ? "none" : "1px solid var(--mist-200)",
                  borderRadius: 20,
                  padding: "32px 28px",
                  position: "relative",
                  boxShadow: t.highlight ? "0 20px 60px rgba(11,20,16,0.18)" : "none",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {t.badge && (
                  <span
                    style={{
                      position: "absolute",
                      top: -14,
                      left: 28,
                      background: "var(--strike-500)",
                      color: "#fff",
                      padding: "5px 12px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {t.badge}
                  </span>
                )}
                <div className="display" style={{ fontSize: 30, marginBottom: 6, color: t.highlight ? "#fff" : "var(--ink-900)" }}>{t.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                  <span className="display" style={{ fontSize: (price || "").length > 5 ? 40 : 60, lineHeight: 1, color: t.highlight ? "#fff" : "var(--ink-900)" }}>{price}</span>
                </div>
                <div style={{ fontSize: 13, color: t.highlight ? "rgba(255,255,255,0.7)" : "var(--ink-500)", marginBottom: 24 }}>{sub}</div>
                <CTAButton
                  href={`/register?plan=${(t.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}&cycle=${yearly ? "yearly" : "monthly"}`}
                  fallbackHref="/abonnementen"
                  navigate={navigate}
                  className={t.highlight ? "btn btn-strike" : "btn btn-pitch"}
                  testid={`pricing-cta-${t.name.toLowerCase()}`}
                  style={{ width: "100%", marginBottom: 24 }}
                >
                  {t.cta}
                </CTAButton>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                  {(t.features || []).map((f, j) => (
                    <li key={j} style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 10, color: t.highlight ? "rgba(255,255,255,0.9)" : "var(--ink-700)" }}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={t.highlight ? "#D4FF3E" : "#0E7C3A"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// 7. REVIEWS & DOWNLOAD
// ---------------------------------------------------------------
function ReviewsAndDownload({ data }) {
  const reviews = data.items || [];
  return (
    <section
      className="section-pad"
      style={{ padding: `80px ${GUTTER}px`, background: "var(--surface)", borderTop: "1px solid var(--mist-200)" }}
    >
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14, textAlign: "center" }}>{data.eyebrow}</div>
        <h2 className="display section-h2" style={{ fontSize: 48, margin: "0 auto 48px", textAlign: "center", maxWidth: 700 }}>{data.headline}</h2>
        <div className="reviews-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 48 }}>
          {reviews.map((r, i) => (
            <Reveal
              key={i}
              delay={i * 120}
              style={{ background: "var(--chalk-50)", border: "1px solid var(--mist-200)", borderRadius: 20, padding: "28px 28px 26px" }}
            >
              <div style={{ display: "flex", gap: 3, marginBottom: 14, color: "var(--strike-500)" }}>
                {[0, 1, 2, 3, 4].map((s) => <Icon key={s} name="star" size={18} color="var(--strike-500)" />)}
              </div>
              <div style={{ fontSize: 18, lineHeight: 1.4, color: "var(--ink-900)", marginBottom: 22, fontWeight: 500 }}>{r.quote}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    background: r.color || "var(--pitch-600)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 15,
                    fontFamily: "var(--sportly-font-display)",
                  }}
                >
                  {r.initials}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{r.name}</div>
                  <div style={{ fontSize: 13, color: "var(--ink-500)" }}>{r.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div style={{ borderTop: "1px solid var(--mist-200)", paddingTop: 40, display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
          <div className="display" style={{ fontSize: 28 }}>{data.download_title}</div>
          <a href={data.apple_href || "#"} style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--ink-900)", color: "#fff", padding: "12px 22px", borderRadius: 14 }}>
            <Icon name="apple" size={22} color="#fff" />
            <div style={{ textAlign: "left", lineHeight: 1.1 }}>
              <div style={{ fontSize: 10, opacity: 0.7 }}>{data.apple_subtitle}</div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{data.apple_label}</div>
            </div>
          </a>
          <a href={data.google_href || "#"} style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--ink-900)", color: "#fff", padding: "12px 22px", borderRadius: 14 }}>
            <Icon name="google" size={22} color="#fff" />
            <div style={{ textAlign: "left", lineHeight: 1.1 }}>
              <div style={{ fontSize: 10, opacity: 0.7 }}>{data.google_subtitle}</div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{data.google_label}</div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// 8. FAQ
// ---------------------------------------------------------------
function FAQ({ data }) {
  const items = data.items || [];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="section-pad" style={{ padding: `80px ${GUTTER}px 40px`, background: "var(--chalk-50)" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14 }}>{data.eyebrow}</div>
          <h2 className="display section-h2" style={{ fontSize: 48 }}>{data.headline}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map((it, i) => (
            <Reveal
              key={i}
              delay={Math.min(i * 60, 360)}
              style={{ background: "var(--surface)", border: "1px solid var(--mist-200)", borderRadius: 16, overflow: "hidden" }}
            >
              <button
                type="button"
                aria-expanded={open === i}
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-item-${i}`}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "18px 22px",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "var(--sportly-font-body)",
                  textAlign: "left",
                  color: "var(--ink-900)",
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600 }}>{it.question}</span>
                <span style={{ transform: open === i ? "rotate(180deg)" : "rotate(0)", transition: "transform 160ms", color: "var(--ink-500)" }}>
                  <Icon name="chevron" size={20} />
                </span>
              </button>
              {open === i && (
                <div style={{ padding: "0 22px 20px", fontSize: 15, color: "var(--ink-700)", lineHeight: 1.55 }}>{it.answer}</div>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// 8b. FOOTER CTA
// ---------------------------------------------------------------
function FooterCTA({ data, navigate }) {
  return (
    <section className="section-pad" style={{ padding: `48px ${GUTTER}px 80px`, background: "var(--chalk-50)" }}>
      <div style={{ maxWidth: MAX, margin: "0 auto" }}>
        <div style={{ background: "var(--ink-900)", color: "#fff", borderRadius: 24, padding: "56px 48px", position: "relative", overflow: "hidden" }}>
          <svg viewBox="0 0 1200 400" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.08 }}>
            <g stroke="#fff" strokeWidth="2" fill="none">
              <circle cx="1000" cy="200" r="160" />
              <circle cx="1000" cy="200" r="50" />
              <line x1="800" y1="0" x2="800" y2="400" />
            </g>
          </svg>
          <div className="footer-cta-grid" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "center", position: "relative" }}>
            <div>
              <div className="eyebrow" style={{ color: "var(--volt-400)", marginBottom: 14 }}>{data.eyebrow}</div>
              <h2 className="display section-h2" style={{ fontSize: 54, lineHeight: 0.95, color: "#fff" }}>
                {data.headline_line1}<br />{data.headline_line2}
              </h2>
              <p style={{ fontSize: 17, color: "rgba(255,255,255,0.78)", marginTop: 18, maxWidth: 520 }}>
                {data.paragraph}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
              <CTAButton
                href={data.primary_href}
                fallbackHref="/abonnementen"
                navigate={navigate}
                className="btn btn-strike"
                testid="footer-cta-primary"
              >
                {data.primary_cta} <Icon name="arrow" size={18} />
              </CTAButton>
              <a href={data.secondary_href || "#functies"} className="btn btn-ghost-dark">
                <Icon name="play" size={16} /> {data.secondary_cta}
              </a>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 8 }}>{data.reassurance}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// 9. ABOUT US
// ---------------------------------------------------------------
function AboutUs({ data }) {
  const stats = data.stats || [];
  return (
    <section
      id="over"
      className="section-pad"
      style={{ padding: `80px ${GUTTER}px 96px`, background: "var(--surface)", borderTop: "1px solid var(--mist-200)" }}
    >
      <div className="about-grid" style={{ maxWidth: MAX, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
        <div>
          <div className="eyebrow" style={{ color: "var(--strike-500)", marginBottom: 14 }}>{data.eyebrow}</div>
          <h2 className="display section-h2" style={{ fontSize: 54, margin: "0 0 22px", lineHeight: 0.95 }}>
            {data.headline_line1}<br />
            <span style={{ color: "var(--pitch-600)" }}>{data.headline_line2}</span>
          </h2>
          <p style={{ fontSize: 17, color: "var(--ink-700)", lineHeight: 1.6, margin: "0 0 18px", maxWidth: 520 }}>
            {data.paragraph1}
          </p>
          <p style={{ fontSize: 17, color: "var(--ink-700)", lineHeight: 1.6, margin: "0 0 28px", maxWidth: 520 }}>
            {data.paragraph2}
          </p>
          <div style={{ display: "flex", gap: 36, paddingTop: 18, borderTop: "1px solid var(--mist-200)", flexWrap: "wrap" }}>
            {stats.map((s, i) => (
              <div key={i}>
                <div className="display" style={{ fontSize: 36, lineHeight: 1 }}>{s.number}</div>
                <div style={{ fontSize: 13, color: "var(--ink-500)", marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            aspectRatio: "4 / 5",
            borderRadius: 24,
            overflow: "hidden",
            background: "linear-gradient(135deg, var(--pitch-50), var(--chalk-100))",
            border: "1px solid var(--mist-200)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {data.team_image ? (
            <img src={data.team_image} alt="Sportly team" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-500)", textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "center" }}>
              Teamfoto
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// FOOTER
// ---------------------------------------------------------------
function Footer({ data, nav }) {
  return (
    <footer className="section-pad" style={{ background: "var(--surface)", borderTop: "1px solid var(--mist-200)", padding: `40px ${GUTTER}px 32px` }}>
      <div style={{ maxWidth: MAX, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "var(--pitch-600)",
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--sportly-font-display)",
              fontWeight: 900,
              fontSize: 18,
              color: "#F7F5EE",
            }}
          >
            {nav?.logo_letter || "S"}
            <span
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "var(--strike-500)",
                border: "2px solid var(--surface)",
              }}
            />
          </div>
          <span style={{ fontSize: 14, color: "var(--ink-500)" }}>{data.copyright}</span>
        </div>
        <div style={{ display: "flex", gap: 28, fontSize: 14, color: "var(--ink-700)" }}>
          {(data.links || []).map((l, i) => (
            <a key={i} href={l.href}>{l.label}</a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------
// Reveal — IntersectionObserver-based fade-up on scroll.
// Adds `is-revealed` once the element enters the viewport.
// ---------------------------------------------------------------
function Reveal({ children, delay = 0, as: Tag = "div", style, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -80px 0px", threshold: 0.1 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`sportly-reveal${shown ? " is-revealed" : ""}`}
      style={{ transitionDelay: shown && delay ? `${delay}ms` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// ---------------------------------------------------------------
// Back-to-top floating button — appears after 600px scroll.
// ---------------------------------------------------------------
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label="Terug naar boven"
      data-testid="back-to-top"
      className={`sportly-back-to-top${visible ? " is-visible" : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 15l-6-6-6 6" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------
export const MarketingHome = () => {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(false);
  const [content, setContent] = useState(null);
  const [checking, setChecking] = useState(true);
  const isAdminPreview = typeof window !== "undefined" && !!localStorage.getItem("hp_admin_token");
  const forcePreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  useEffect(() => {
    let active = true;
    const token = getToken();

    // Enable smooth-scroll behavior for in-page anchor links — only while
    // MarketingHome is mounted so it doesn't affect the app shell.
    document.documentElement.classList.add("sportly-smooth-scroll");

    // Always fetch content (public endpoint)
    axios.get(`${API}/homepage`).then((r) => {
      if (!active) return;
      // Feature flag: if homepage isn't published and visitor isn't an admin,
      // route them to the login page instead. Admins can still preview.
      // Authenticated users are handled by the auth-me effect below (route to app).
      const published = r.data?.published === true;
      const adminPreview = !!localStorage.getItem("hp_admin_token");
      const hasUserToken = !!token;
      if (!published && !adminPreview && !hasUserToken) {
        navigate("/login", { replace: true });
        return;
      }
      setContent(r.data);
      applySeo(r.data?.seo);
    }).catch(() => {
      if (active) setContent({});
    });

    // Skip auth-me redirect only when explicitly previewing via ?preview=1.
    // Note: hp_admin_token alone should NOT block the redirect — admins who also
    // have a regular user session must still be routed to their team dashboard.
    // The CMS "Preview concept" button always appends ?preview=1.
    const skipRedirect = forcePreview;
    if (!token || skipRedirect) {
      setChecking(false);
      return () => { active = false; };
    }

    (async () => {
      try {
        await axios.get(`${API}/auth/me`);
        if (!active) return;
        try {
          const res = await axios.get(`${API}/onboarding/status`);
          if (!active) return;
          navigate(res.data.complete ? "/overview" : "/get-started", { replace: true });
        } catch {
          if (!active) return;
          navigate("/overview", { replace: true });
        }
      } catch {
        if (!active) return;
        setIsAuthed(false);
        setChecking(false);
      }
    })();
    return () => {
      active = false;
      document.documentElement.classList.remove("sportly-smooth-scroll");
    };
  }, [navigate, isAdminPreview, forcePreview]);

  if (checking || !content) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="sportly-home" data-testid="marketing-home">
      {(isAdminPreview || forcePreview) && content?.published !== true && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-slate-950 text-center text-xs sm:text-sm font-semibold py-2 px-4 shadow-md flex items-center justify-center gap-3"
          data-testid="homepage-concept-banner"
        >
          <span>Concept — deze homepage is nog niet live. Alleen admins zien deze preview.</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-slate-800"
            data-testid="homepage-preview-refresh-btn"
            title="Preview verversen om de laatste CMS-wijzigingen te laden"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            Ververs
          </button>
          <a
            href="/sportly-admin/homepage"
            className="underline underline-offset-2 hover:text-slate-800 hidden sm:inline"
          >
            Terug naar CMS
          </a>
        </div>
      )}
      <Nav data={content.nav || {}} isAuthed={isAuthed} navigate={navigate} />
      <Hero data={content.hero || {}} navigate={navigate} />
      <SocialProof data={content.social_proof || {}} />
      <Features data={content.features || {}} />
      <HowItWorks data={content.how_it_works || {}} />
      <Pricing data={content.pricing || {}} navigate={navigate} />
      <ReviewsAndDownload data={content.reviews || {}} />
      <FAQ data={content.faq || {}} />
      <FooterCTA data={content.footer_cta || {}} navigate={navigate} />
      <AboutUs data={content.about || {}} />
      <Footer data={content.footer || {}} nav={content.nav || {}} />
      <BackToTop />
    </div>
  );
};

export default MarketingHome;
